//import { UserOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { Form, notification } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GQL_REGISTER } from '~/gql/auth/auth';
import { Payload, useAuth } from '~/helpers/store/auth';
import { Role, routingDefault } from '~/pages/utils/router';
import { parseJwt } from '~/utils/jwt';
import { WrapperItem } from '~/components/ui/wrapper-item';
import Button from '~/components/ui/button';
import ButtonText from '~/components/ui/button-text';
import GameLayout from '~/pages/authentified/GameLayout';
import { SFormItem, SInput, SSpan } from '~/components/styles/styles';
import { GQL_AVATARS, StoredAvatar } from '~/gql/avatars/avatars';
import { authClient } from '~/helpers/apollo';
import { classic } from '~/themes';
import { AvatarItem } from '~/components/ui/avatar-item';
import useGameStore from '~/helpers/store/game/game';

function CreateAccountGame() {
  const { setAvatarFileName } = useGameStore();
  const [username, setUsername] = useState('');
  const { setPayload } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const [avatars, setAvatars] = useState<StoredAvatar[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<StoredAvatar | null>(
    null
  );

  const [form] = Form.useForm();

  //const { changeLanguage } = useTranslations();

  //   const handleLanguageChange = (newLocale: string) => {
  //     changeLanguage(newLocale);
  //   };

  useQuery(GQL_AVATARS, {
    client: authClient,
    onCompleted: (data) => {
      if (data) {
        setAvatars(data.getAvatars);
      }
    },
  });

  useEffect(() => {
    setSelectedAvatar(null);
  }, []);

  const [registerAccount] = useMutation(GQL_REGISTER, {
    onCompleted: (data) => {
      if (data.register.__typename === 'AuthReponse') {
        const dataJwt = parseJwt(data.register.tokens.access_token);
        if (!dataJwt) {
          return;
        }
        const payload: Payload = {
          access_token: data.register.tokens.access_token,
          refresh_token: data.register.tokens.refresh_token,
          ...dataJwt,
        };

        setPayload(payload);
        if (data) {
          setAvatarFileName(data.register.user.avatar?.fileName);
        }
        if (data.register.user.isChallengeable === null) {
          navigate('/first-connection');
        } else {
          routingDefault(payload.roles as Role, navigate);
        }
      }
      if (data.register.__typename === 'CustomError') {
        if (data.register.message === 'User already exists') {
          form.setFields([
            {
              name: 'username',
              errors: [i18n._('register.username.exists')],
            },
          ]);
        }
      }
    },
    onError: () => {
      notification.error({
        message: i18n._('common.login'),
        description: i18n._('bad.server-login.message'),
        placement: 'bottomLeft',
      });
    },
  });

  const registerHandler = async () => {
    if (selectedAvatar)
      await registerAccount({
        variables: { username, avatarId: selectedAvatar?.id },
      });
  };

  function handleAvatarClick(avatar: StoredAvatar) {
    if (selectedAvatar && selectedAvatar.id === avatar.id) {
      setSelectedAvatar(null);
    } else {
      setSelectedAvatar(avatar);
    }
  }

  function handleChangeUserName(name: string) {
    setUsername(name.slice(0, 12));
  }

  //if (loading) return <p>{i18n._('common.loading')} ...</p>;

  return (
    <GameLayout backgroundSize="cover" withoutFooter withoutHeader>
      <WrapperItem
        widthLandscape="40%"
        title={`${i18n._('common.action.create.account.title')}`}
      >
        <SFormLogin
          form={form}
          onFinish={registerHandler}
          initialValues={{ remember: true }}
        >
          <SSpan $color={classic.token?.colorText}>
            {i18n._('common.choose.avatar')}
          </SSpan>
          <SDivAvatars>
            {avatars.map((avatar) => (
              <AvatarItem
                key={avatar.id}
                avatar={avatar}
                selected={
                  selectedAvatar !== null && selectedAvatar.id === avatar.id
                }
                onClick={() => handleAvatarClick(avatar)}
              ></AvatarItem>
            ))}
          </SDivAvatars>

          <SSpan $color={classic.token?.colorText}>
            {i18n._('common.choose.pseudo')}
          </SSpan>
          <SFormItem
            name="username"
            className="customInput"
            rules={[
              {
                required: true,
                message: i18n._('input.username.message'),
              },
            ]}
          >
            <SInput
              suffix={`${username.length}/12`}
              placeholder={i18n._('common.username')}
              onChange={(e) => handleChangeUserName(e.target.value)}
              size="large"
              value={username}
              maxLength={12}
            />
          </SFormItem>

          <SFormItem>
            <Button
              color="primary"
              type="submit"
              disabled={username.length === 0 || !selectedAvatar}
            >
              {i18n._('common.valid')}
            </Button>
          </SFormItem>

          <SFormItem $lastItem>
            <ButtonText
              color="primary"
              type="button"
              onClick={() => navigate('/')}
              textTransform="uppercase"
            >
              {i18n._('common.back')}
            </ButtonText>
          </SFormItem>
        </SFormLogin>
      </WrapperItem>
    </GameLayout>
  );
}

export default CreateAccountGame;

const SDivAvatars = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 20px;
  width: 100%;
  @media screen and (max-height: 450px) {
    margin-bottom: 5px;
  }
  @media screen and (max-width: 450px) {
    margin-bottom: 10px;
  }
`;

export const SFormLogin = styled(Form)`
  min-width: 30vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 15px;

  @media (max-width: 992px) {
    & .customInput {
      width: 100%;
    }
  }
`;

export const LoginForm = styled.div`
  justify-content: center;
  align-items: center;
  width: 60%;
  @media (max-width: 992px) {
    height: auto;
    width: 95%;
  }
`;

export const SForm = styled(Form)`
  min-width: 30vw;
  height: 100%;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
